import React, { memo, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { FilterableSelect } from '../../../../components/ui/FilterableSelect';
import TextField from './helpers/TextField';

const subtitleClass = 'text-sm font-bold text-ninja-dark dark:text-white mb-3';

const Bootic = ({ ecommerce, warehouses, onSave, loading, saving = false, isEdit }) => {
  const [formData, setFormData] = useState(ecommerce);
  const [deliveryInput, setDeliveryInput] = useState("");

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        authentication: {
          ...prev.configuration.authentication,
          [field]: value,
        },
      },
    }));
  };

  const handleWarehouseChange = (value) => {
    setFormData({ ...formData, warehouse: value });
  };

  const addDeliveryMethod = () => {
    if (!deliveryInput) return;
    const currentMethods =
      formData.configuration.options.enabled_delivery_methods || [];
    if (!currentMethods.includes(deliveryInput)) {
      setFormData((prev) => ({
        ...prev,
        configuration: {
          ...prev.configuration,
          options: {
            ...prev.configuration.options,
            enabled_delivery_methods: [...currentMethods, deliveryInput],
          },
        },
      }));
    }
    setDeliveryInput("");
  };

  const removeDeliveryMethod = (method) => {
    const currentMethods =
      formData.configuration.options.enabled_delivery_methods.filter(
        (m) => m !== method,
      );
    setFormData((prev) => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        options: {
          ...prev.configuration.options,
          enabled_delivery_methods: currentMethods,
        },
      },
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const deliveryMethods = formData.configuration.options.enabled_delivery_methods || [];

  return (
    <form data-testid="ecommerce-form" data-platform="bootic">
      <h3 className="text-lg font-bold text-ninja-dark dark:text-white mb-5">Configuración Bootic</h3>

      <div className="bg-blue-50 dark:bg-blue-900/20 text-info p-3 rounded-xl border border-blue-200 dark:border-blue-900 text-sm mb-5">
        Configura los tokens de acceso y luego copia las URLs de los Webhooks
        para configurarlas en tu panel de administración de Bootic.
      </div>

      <div className="mb-5">
        <FilterableSelect
          variant="form"
          testId="ecommerce-warehouse"
          label="Bodega Asociada"
          value={formData.warehouse || ""}
          onChange={handleWarehouseChange}
          options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
          placeholder="Seleccionar..."
        />
      </div>

      <p className={subtitleClass}>Credenciales API</p>
      <div className="space-y-4 mb-5">
        <TextField
          label="Token (Access Token)"
          value={formData.configuration.authentication.token || ""}
          onChange={(e) => handleChange("token", e.target.value)}
        />
        <TextField
          label="Refresh Token"
          value={formData.configuration.authentication.refresh_token || ""}
          onChange={(e) => handleChange("refresh_token", e.target.value)}
        />
      </div>

      <div className="h-px bg-border my-6" />

      <p className={subtitleClass}>Webhooks</p>

      <div className="space-y-4">
        <TextField
          id="create_order"
          name="create_order"
          label="Webhook Crear Orden"
          value={ecommerce.configuration.create_order.url}
          disabled
        />
        <TextField
          id="cancel_order"
          name="cancel_order"
          label="Webhook Cancelar Orden"
          value={ecommerce.configuration.cancel_order.url}
          disabled
        />
        <TextField
          id="update_order"
          name="update_order"
          label="Webhook Actualizar Orden"
          value={ecommerce.configuration.update_order.url}
          disabled
        />
      </div>

      <div data-testid="ecommerce-delivery-methods" className="mt-8 mb-5 border border-border rounded-xl p-4">
        <span className="block text-sm font-semibold text-ninja-dark dark:text-white mb-2">
          Métodos de Envío Habilitados
        </span>
        <div className="flex items-center gap-2 mb-3">
          <TextField
            className="flex-1"
            aria-label="Método de envío"
            placeholder="Ej: Starken, Chilexpress..."
            value={deliveryInput}
            onChange={(e) => setDeliveryInput(e.target.value)}
          />
          <Button type="button" variant="primary" onClick={addDeliveryMethod} disabled={!deliveryInput}>
            Agregar
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {deliveryMethods.length > 0 ? (
            deliveryMethods.map((m) => (
              <span
                key={m}
                data-testid="ecommerce-delivery-method"
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full border border-ninja-green text-ninja-green text-sm"
              >
                {m}
                <button type="button" aria-label={`Quitar ${m}`} onClick={() => removeDeliveryMethod(m)} className="hover:text-danger">
                  <X size={14} />
                </button>
              </span>
            ))
          ) : (
            <span className="text-sm text-text-muted">No hay métodos configurados</span>
          )}
        </div>
      </div>

      <Button
        type="submit"
        variant="primary"
        size="lg"
        fullWidth
        disabled={loading}
        loading={saving}
        loadingLabel={isEdit ? "Actualizando..." : "Agregando..."}
        onClick={handleSubmit}
      >
        {isEdit ? "Actualizar Integración" : "Agregar Integración"}
      </Button>
    </form>
  );
};

export default memo(Bootic);
